/**
 * AlbumPlayer — compact, inline music player for an artist's tracks.
 * Designed to live INSIDE an Artist card (collapsed by default) so the
 * "Listen" experience travels with the artist instead of needing a
 * separate Music Room page.
 *
 * - Shows album art + track count
 * - "▶ Listen" toggles the tracklist open
 * - Click any track to play (30s previews when the file is short, full when not)
 * - Single global "currently playing" state so the user only hears one at a time
 *   — when ANY player starts playing, every other player on the page auto-
 *   collapses + pauses (module-level pub/sub below). This lets the user
 *   browse multiple artist cards on the same page and only ever hear one.
 */
import { useState, useRef, useEffect } from "react";
import { Play, Pause, Music as MusicIcon } from "lucide-react";

// ── Module-level coordinator: any number of AlbumPlayer instances can mount
// on the same page; only one should be "active" at a time. We broadcast
// the active player's instance id, and every other instance reacts by
// pausing its audio and collapsing its tracklist.
const playerSubscribers = new Set();
const announceActive = (id) => {
  playerSubscribers.forEach((cb) => cb(id));
};

let _nextId = 0;
const newInstanceId = () => `albumplayer-${++_nextId}`;

const AlbumPlayer = ({ tracks = [], artistName, accentColor = "#9B59B6", compact = false }) => {
  const [open, setOpen] = useState(!compact); // big on artist detail, collapsed on cards
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);
  // Stable per-instance id so we can ignore our own broadcasts
  const instanceIdRef = useRef(null);
  if (instanceIdRef.current === null) instanceIdRef.current = newInstanceId();
  const instanceId = instanceIdRef.current;

  // Albums grouped by `album` field for visual grouping
  const albums = tracks.reduce((acc, t) => {
    const k = t.album || "Singles";
    if (!acc[k]) acc[k] = { name: k, art: t.album_art_url, tracks: [] };
    if (!acc[k].art && t.album_art_url) acc[k].art = t.album_art_url;
    acc[k].tracks.push(t);
    return acc;
  }, {});

  const albumList = Object.values(albums);
  const trackCount = tracks.length;

  // Subscribe to "another player became active" broadcasts. When the active
  // id is NOT ours, collapse the tracklist and stop any audio we're playing.
  useEffect(() => {
    const onActive = (activeId) => {
      if (activeId === instanceId) return;
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch (_) { /* noop */ }
      }
      setPlayingId(null);
      if (compact) setOpen(false);
    };
    playerSubscribers.add(onActive);
    return () => { playerSubscribers.delete(onActive); };
  }, [instanceId, compact]);

  // Pause when component unmounts (route change etc.)
  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const play = (track) => {
    if (playingId === track.item_id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    // Claim "active player" so any other AlbumPlayer on the page collapses.
    announceActive(instanceId);
    setPlayingId(track.item_id);
    // The audio element re-renders with new src; ensure play after a tick
    setTimeout(() => audioRef.current?.play().catch(() => {}), 50);
  };

  // Toggle the tracklist. Opening it also claims active so any other expanded
  // player on the page collapses — keeps the page calm with one card "in focus."
  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) announceActive(instanceId);
  };

  if (trackCount === 0) return null;

  return (
    <div className="border-t border-[#F0EFEB] mt-3 pt-3" data-testid={`album-player-${artistName?.replace(/\s+/g, "-").toLowerCase()}`}>
      {/* Album thumbnail strip — small previews of each album */}
      <button
        type="button"
        onClick={toggleOpen}
        className="w-full flex items-center gap-3 text-left hover:bg-[#FAF9F6] rounded-lg p-2 -mx-2 transition-colors border-0 bg-transparent cursor-pointer"
        data-testid="album-player-toggle"
      >
        <div className="flex -space-x-2">
          {albumList.slice(0, 3).map((a, i) => (
            <div
              key={a.name}
              className="w-10 h-10 rounded border-2 border-white bg-[#1A1A2E] overflow-hidden flex-shrink-0 shadow-sm"
              style={{ zIndex: 3 - i }}
            >
              {a.art ? (
                <img src={a.art} alt={a.name} className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/40">
                  <MusicIcon className="h-5 w-5" />
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-widest text-[#5C5C5C] m-0">
            {open ? "Hide tracks" : "Listen"}
          </p>
          <p className="text-[11px] text-[#5C5C5C] m-0">
            {trackCount} {trackCount === 1 ? "track" : "tracks"}
            {albumList.length > 1 ? ` · ${albumList.length} albums` : ` · ${albumList[0].name}`}
          </p>
        </div>
        <Play className="h-4 w-4 flex-shrink-0" style={{ color: accentColor }} />
      </button>

      {/* Expanded tracklist — grouped by album */}
      {open && (
        <div className="mt-3 space-y-3 max-h-[420px] overflow-y-auto" data-testid="album-player-list">
          {albumList.map(album => (
            <div key={album.name}>
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-7 h-7 rounded bg-[#1A1A2E] overflow-hidden flex-shrink-0">
                  {album.art && <img src={album.art} alt={album.name} className="w-full h-full object-cover" loading="lazy" />}
                </div>
                <p className="font-['Playfair_Display'] text-sm font-semibold text-[#1A1A1A] m-0 truncate">{album.name}</p>
                <span className="text-[10px] text-[#5C5C5C] uppercase tracking-wider">{album.tracks.length}</span>
              </div>
              <ul className="m-0 p-0 list-none divide-y divide-[#F0EFEB]">
                {album.tracks.map((t, idx) => {
                  const isPlaying = playingId === t.item_id;
                  return (
                    <li key={t.item_id} className="py-1.5">
                      <button
                        type="button"
                        onClick={() => t.file_url && play(t)}
                        disabled={!t.file_url}
                        className={`w-full flex items-center gap-2 text-left rounded-md px-2 py-1 transition-colors border-0 bg-transparent ${t.file_url ? 'cursor-pointer hover:bg-[#FAF9F6]' : 'cursor-default opacity-50'}`}
                        data-testid={`album-track-${t.item_id}`}
                      >
                        <span
                          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white"
                          style={{ backgroundColor: isPlaying ? accentColor : "#E2E0DC" }}
                        >
                          {isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" style={{ color: t.file_url ? "#5C5C5C" : "#AAA" }} />}
                        </span>
                        <span className="text-[10px] text-[#9C9C9C] font-mono w-5 text-right flex-shrink-0">{(idx + 1).toString().padStart(2, "0")}</span>
                        <span className="flex-1 min-w-0">
                          <span className={`text-xs ${isPlaying ? "font-semibold" : "font-medium"} text-[#1A1A1A] truncate block`}>
                            {t.title}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Hidden audio element — single shared player */}
      {playingId && (() => {
        const t = tracks.find(x => x.item_id === playingId);
        if (!t || !t.file_url) return null;
        const src = t.file_url.startsWith("http") ? t.file_url : `${process.env.REACT_APP_BACKEND_URL}${t.file_url}`;
        return (
          <audio
            ref={audioRef}
            src={src}
            controls
            autoPlay
            onEnded={() => setPlayingId(null)}
            className="w-full mt-3 rounded"
            data-testid="album-player-audio"
          />
        );
      })()}
    </div>
  );
};

export default AlbumPlayer;
